import { Component, signal } from '@angular/core';
import { filter, map, Observable, takeUntil, tap } from "rxjs";
import { ThirdPartySettings } from "@store/third-party/third-party.state";
import { currentOpenAiSettings } from "@store/third-party/open-ai-settings/open-ai-settings.selector";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { OpenAiSettingDialogNewComponent } from "../dialogs/open-ai-setting-dialog-new/open-ai-setting-dialog-new.component";
import { BaseComponent } from "../../base.component";
import { ActivatedRoute, Router } from "@angular/router";
import { CustomAiPromptDialogComponent } from '../dialogs/custom-ai-prompt-dialog/custom-ai-prompt-dialog.component';
import { AiAssistantStore } from '@store/component/ai-assistant.store';
import { Prompt } from '@models/prompt.model';
import { currentPrompts } from '@store/ai-insights/prompts/prompts.selector';
import { currentSummaryPrompts } from '@store/ai-insights/summary-prompts/summary-prompts.selector';
import { summaryPromptsActions } from '@store/ai-insights/summary-prompts/summary-prompts.actions';

@Component({
    selector: 'lex-doc-viewer-bottom-panel',
    templateUrl: './doc-viewer-bottom-panel.component.html',
    styleUrl: './doc-viewer-bottom-panel.component.scss',
    standalone: false
})
export class DocViewerBottomPanelComponent extends BaseComponent {
	public openAiSettings$: Observable<ThirdPartySettings> = this.store.select(currentOpenAiSettings());
	public currentTab$ = signal<'summary' | 'co-pilot'>('co-pilot')
	public collapsed: boolean = true;
	public panelHeight: number = 50;
	public panelMinHeight: number = 50;
	public promptTooltip: string = 'No summary prompt selected';
	public summaryPrompts: Prompt[] = [];
	public summaryPromptId: string = '';

	public isLinked$: Observable<boolean> = this.openAiSettings$.pipe(
		map((settings) => {
			return settings.isLinked
		}));

	private selectedPrompt: Prompt = null;
	private globalPrompts$: Observable<Prompt[]> = this.store.select(currentPrompts());
	private globalPrompts: Prompt[];
	private summaryPrompts$: Observable<Prompt[]> = this.store.select(currentSummaryPrompts());

	constructor(
		private dialog: MatDialog,
		private store: Store,
		private router: Router,
		private route: ActivatedRoute,
		private aiAssistantStore: AiAssistantStore) {
		super();

		this.route.queryParams.pipe(
			map(x => x.ai as 'summary' | 'co-pilot'),
			takeUntil(this.destroy$)
		).subscribe((tab) => {
			this.currentTab$.set(tab || 'co-pilot');
			if (tab === 'summary')
				this.store.dispatch(summaryPromptsActions.loadPrompts())
		});

		this.globalPrompts$.pipe(
			filter((prompts) => !!prompts),
			map((prompts) => {
				this.aiAssistantStore.addPrompts(prompts)
				return prompts
			}),
			tap((prompts) => this.globalPrompts = prompts),
			takeUntil(this.destroy$)
		).subscribe();

		this.summaryPrompts$.pipe(
			tap((prompts) => {
				this.summaryPrompts = prompts;
				if (this.summaryPromptId && this.summaryPrompts.filter(x => x.promptId === this.summaryPromptId).length === 0) {
					this.summaryPromptId = '';
					this.promptTooltip = 'No summary prompt selected';
				}
				else if (this.summaryPromptId) {
					const prompt = this.summaryPrompts.filter(x => x.promptId === this.summaryPromptId);
					this.selectSummaryPrompt(prompt[0]);
				}
				else if (this.summaryPrompts.length === 1) {
					this.selectSummaryPrompt(this.summaryPrompts[0]);
				}
				else if (this.summaryPrompts.length === 0) {
					this.summaryPromptId = '';
					this.promptTooltip = '';
				}
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.aiAssistantStore.currentPrompt$.pipe(
			map((current) => this.selectedPrompt = current),
			takeUntil(this.destroy$)
		).subscribe();

	}

	public go(t: 'summary' | 'co-pilot') {
		if (this.collapsed)
			this.collapsePanel();
		this.router.navigate(["."], {
			queryParams: {
				ai: t
			},
			queryParamsHandling: "merge",
			relativeTo: this.route
		});
	}

	public collapsePanel() {
		this.collapsed = !this.collapsed;
		if (this.collapsed) {
			this.panelMinHeight = 50;
			this.panelHeight = 50;
		}
		else {
			this.panelHeight = 30;
			this.panelMinHeight = 200;
		}
	}

	public selectSummaryPrompt(prompt: Prompt): void {
		if (this.summaryPromptId !== prompt.promptId)
			this.summaryPromptId = prompt.promptId;
		this.promptTooltip = `${prompt.title}: ${prompt.fullPrompt}`;
	}

	public openPromptSetting(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width = '900px';
		dialogConfig.maxHeight = '480px';
		dialogConfig.autoFocus = false;
		dialogConfig.data = this.selectedPrompt;
		const dialogObservable = this.dialog.open(CustomAiPromptDialogComponent, dialogConfig).afterClosed();

		dialogObservable.pipe(
			tap(() => {
				if (this.selectedPrompt) {
					this.aiAssistantStore.refreshCurrentPrompt();
				}
				else {
					this.aiAssistantStore.clearPrompts();
					this.aiAssistantStore.addPrompts(this.globalPrompts);
				}
				this.store.dispatch(summaryPromptsActions.loadPrompts());
			})).subscribe();
	}

	public linkOpenAi(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		this.dialog.open(OpenAiSettingDialogNewComponent, dialogConfig);
	}
}
